import {
  QuizConfigProvider,
  useQuizMetaContext,
  useQuizResults,
  useQuizStatus,
  useSocketIoConnection,
} from '@mentimeter/quiz';
import type { QuizResultData } from '@mentimeter/core-hooks';
import { useEffect, useMemo } from 'react';
import { ClientErrorCodes, GameStateEnums } from '@mentimeter/quiz-types';
import type { QuizChoiceStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Loading } from '../../shared/components/quiz/loading';
import { Error } from '../../shared/components/quiz/error';
import { FollowerPresentation } from './FollowerPresentation';
import { LivePresentation } from './LivePresentation';
import { useQuizChoicesPreviewResultsLegacy } from './hooks/use-quiz-choices-preview-results-legacy';

export const LivePresentationDataHandler = () => {
  const { useCurrentSession, useVotingInstructions, isPreviewResultsEnabled } =
    usePresentationContext();
  const { voteKey } = useVotingInstructions();

  const slide = usePresentationSlideContext();
  const { data, error, mutate } = useQuizResults<QuizResultData>();
  const { currentSessionId: session } = useCurrentSession();
  const { connection } = useSocketIoConnection();
  const { timeOffset } = useQuizStatus(connection);
  const { follower } = useQuizMetaContext();

  const interactiveContent = slide.interactiveContents[0]!;
  const staticContent = slide.staticContent as QuizChoiceStaticContent;

  const shouldShowLegacyImage =
    staticContent.image.imageLayoutPosition.end === 0 &&
    staticContent.image.imageLayoutPosition.start === 0;

  // We can have total scores, but we need to determine for this question
  const hasResultsForQuestion = Boolean(
    data?.some((r) => Boolean(r.responses)),
  );

  const transformedChoices = useMemo(() => {
    return interactiveContent.choices.map((choice, index) => ({
      id: choice.legacyChoiceId ?? index + 1,
      label: choice.title,
    }));
  }, [interactiveContent.choices]);

  const previewResults = useQuizChoicesPreviewResultsLegacy({
    isPreviewResultsEnabled,
    choices: transformedChoices,
  });

  const resultsToShow = isPreviewResultsEnabled ? previewResults : data;

  // Invalidate results when session is updated
  useEffect(() => {
    return () => void mutate(undefined);
  }, [session, mutate]);

  if (
    !resultsToShow ||
    !connection ||
    session === undefined ||
    timeOffset === undefined
  ) {
    return (
      <CenteringContainer>
        <Loading />
      </CenteringContainer>
    );
  }

  if (error) {
    return (
      <CenteringContainer>
        <Error errorCode={ClientErrorCodes.RESULTS_FETCH} />
      </CenteringContainer>
    );
  }

  if (follower) {
    return (
      <QuizConfigProvider
        questionPublicKey={slide.slidePublicKey}
        questionAdminKey={slide.slideAdminKey}
        voteKey={voteKey}
      >
        <FollowerPresentation
          activeSession={session}
          imageUrl={slide.images?.[0]?.presets.medium.url}
          timeBasedScoring={interactiveContent.scoring === 'time_based'}
          title={interactiveContent.title}
          styledTitle={staticContent.styledTitle as Record<string, unknown>}
          label={staticContent.label}
          description={interactiveContent.description}
          choices={interactiveContent.choices}
          shouldShowLegacyImage={shouldShowLegacyImage}
          textSizeOffset={staticContent.textSizeOffset}
          data={resultsToShow ?? []}
          isPreviewResults={isPreviewResultsEnabled}
        />
      </QuizConfigProvider>
    );
  }

  return (
    <QuizConfigProvider
      questionPublicKey={slide.slidePublicKey}
      questionAdminKey={slide.slideAdminKey}
      voteKey={voteKey}
    >
      <LivePresentation
        activeSession={session}
        initialGameState={
          hasResultsForQuestion ? GameStateEnums.RESULT : GameStateEnums.LOBBY
        }
        imageUrl={slide.images?.[0]?.presets.medium.url}
        timeBasedScoring={interactiveContent.scoring === 'time_based'}
        title={interactiveContent.title}
        styledTitle={staticContent.styledTitle as Record<string, unknown>}
        label={staticContent.label}
        description={interactiveContent.description}
        countdownToVote={interactiveContent.countdown ?? 0}
        choices={interactiveContent.choices}
        shouldShowLegacyImage={shouldShowLegacyImage}
        textSizeOffset={staticContent.textSizeOffset}
        data={resultsToShow ?? []}
        isPreviewResults={isPreviewResultsEnabled}
      />
    </QuizConfigProvider>
  );
};
